import React from 'react';
import { Button, NativeSelect } from '@tyndo/ui-kit';
import {
  Settings as SettingsIcon,
  Trash2,
  Copy,
  GripVertical,
  X,
  Maximize2,
} from 'lucide-react';
import type { WidgetSize } from '@tyndo/types';
import type {
  LayoutWidgetInstance,
  WidgetDefinition,
} from '../LandingPageEditor/types';
import WidgetTile from './WidgetTile';

const ALL_SIZES: WidgetSize[] = [
  'small',
  'medium',
  'large',
  'extraLarge',
  'wallboard',
];

/** Grid column span per widget size on the 4-column lg grid. */
const COL_SPAN: Record<WidgetSize, string> = {
  small: 'lg:col-span-1',
  medium: 'lg:col-span-2 sm:col-span-2',
  large: 'lg:col-span-3 sm:col-span-2',
  extraLarge: 'lg:col-span-4 sm:col-span-2',
  wallboard: 'lg:col-span-4 sm:col-span-2',
};

export interface WidgetCanvasProps {
  widgets: LayoutWidgetInstance[];
  definitions: WidgetDefinition[];
  editing?: boolean;
  onRemove?: (instanceId: string) => void;
  onDuplicate?: (instanceId: string) => void;
  onResize?: (instanceId: string, size: WidgetSize) => void;
  onConfigure?: (instanceId: string) => void;
  /** HTML5 drag-to-reorder handlers (shared with the editor). */
  onDragStart?: (instanceId: string) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDrop?: (targetInstanceId: string) => void;
  /** Keyboard reorder — Space to grab, Arrow to move, Escape to drop. */
  onHandleKeyDown?: (e: React.KeyboardEvent, instanceId: string) => void;
  grabbedId?: string | null;
}

/**
 * Responsive widget grid. Out of edit mode it renders the live, deferred
 * widgets (the saved layout). In edit mode each tile jiggles and grows
 * direct-manipulation affordances (remove, resize, configure, duplicate,
 * drag/keyboard reorder) — the live widget body is replaced by labelled
 * chrome so adding a widget never shows a premature data preview.
 */
const WidgetCanvas: React.FC<WidgetCanvasProps> = ({
  widgets,
  definitions,
  editing = false,
  onRemove,
  onDuplicate,
  onResize,
  onConfigure,
  onDragStart,
  onDragOver,
  onDrop,
  onHandleKeyDown,
  grabbedId,
}) => {
  const ordered = [...widgets].sort((a, b) => a.order - b.order);
  const defFor = (widgetId: string) =>
    definitions.find((d) => d.widgetId === widgetId);

  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 auto-rows-[minmax(8rem,auto)]"
      data-testid={editing ? 'editor-widget-list' : 'widget-canvas'}
      role={editing ? 'list' : undefined}
    >
      {ordered.map((w, idx) => {
        const def = defFor(w.widgetId);
        const title = def?.label ?? w.widgetId;
        const grabbed = grabbedId === w.instanceId;
        const supported = def?.supportedSizes?.length
          ? def.supportedSizes
          : ALL_SIZES;

        return (
          <div
            key={w.instanceId}
            data-testid={
              editing ? `editor-widget-row-${w.widgetId}` : undefined
            }
            role={editing ? 'listitem' : undefined}
            // The edit-mode jiggle deliberately does NOT live here.
            //
            // It is an infinite ±0.55° rotation, and this element is the
            // positioning context for the edit controls (`absolute -top-2
            // -right-2` remove badge, `-top-2 -left-2` drag handle). Rotating it
            // moves those controls forever: their bounding box never repeats
            // between two frames, so nothing that waits for a target to hold
            // still can ever click them — Playwright reports it verbatim as
            // "element is not stable", and a person aiming at a 24 px badge that
            // never stops moving has the same problem in a form that is harder
            // to name. The animation now sits on the tile's CONTENT layer inside
            // WidgetTile, so the wobble still reads as "editable" while the
            // controls anchored to this box stay where they are.
            className={`${COL_SPAN[w.size] ?? 'lg:col-span-2'} ${
              editing && grabbed ? 'ring-2 ring-primary rounded-2xl' : ''
            }`}
            onDragOver={editing ? onDragOver : undefined}
            onDrop={editing ? () => onDrop?.(w.instanceId) : undefined}
          >
            {editing ? (
              <WidgetTile
                instance={w}
                definition={def}
                editing
                overlay={
                  <EditOverlay
                    instance={w}
                    title={title}
                    index={idx}
                    grabbed={grabbed}
                    supported={supported}
                    onRemove={onRemove}
                    onDuplicate={onDuplicate}
                    onResize={onResize}
                    onConfigure={onConfigure}
                    onDragStart={onDragStart}
                    onHandleKeyDown={onHandleKeyDown}
                  />
                }
              />
            ) : (
              <WidgetTile instance={w} definition={def} />
            )}
          </div>
        );
      })}
    </div>
  );
};

const EditOverlay: React.FC<{
  instance: LayoutWidgetInstance;
  title: string;
  index: number;
  grabbed: boolean;
  supported: WidgetSize[];
  onRemove?: (instanceId: string) => void;
  onDuplicate?: (instanceId: string) => void;
  onResize?: (instanceId: string, size: WidgetSize) => void;
  onConfigure?: (instanceId: string) => void;
  onDragStart?: (instanceId: string) => void;
  onHandleKeyDown?: (e: React.KeyboardEvent, instanceId: string) => void;
}> = ({
  instance,
  title,
  index,
  grabbed,
  supported,
  onRemove,
  onDuplicate,
  onResize,
  onConfigure,
  onDragStart,
  onHandleKeyDown,
}) => {
  const id = instance.instanceId;
  return (
    <>
      {/* Remove badge — top-right */}
      {onRemove && (
        <Button
          type="button"
          variant="ghost"
          onClick={() => onRemove(id)}
          aria-label={`Remove ${title}`}
          data-testid={`editor-widget-remove-${instance.widgetId}`}
          className="whitespace-normal p-0 [&_svg]:size-3.5 glass-strong absolute -top-2 -right-2 z-10 flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground hover:text-foreground shadow-[var(--shadow-overlay)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      )}

      {/* Drag handle — top-left */}
      <Button
        type="button"
        variant="ghost"
        draggable
        onDragStart={() => onDragStart?.(id)}
        onKeyDown={(e) => onHandleKeyDown?.(e, id)}
        aria-pressed={grabbed}
        aria-label={`Drag handle for ${title}. ${
          grabbed
            ? 'Grabbed. Use arrow up and arrow down to reorder, Escape to cancel.'
            : 'Press Space to grab.'
        }`}
        data-testid={`editor-widget-handle-${instance.widgetId}`}
        className="whitespace-normal p-0 [&_svg]:size-3.5 glass-strong absolute -top-2 -left-2 z-10 flex h-6 w-6 cursor-grab items-center justify-center rounded-full text-muted-foreground hover:text-foreground active:cursor-grabbing shadow-[var(--shadow-overlay)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <GripVertical className="h-3.5 w-3.5" aria-hidden="true" />
      </Button>

      {/* Bottom control bar — size, configure, duplicate */}
      <div className="glass-strong absolute bottom-2 left-2 right-2 z-10 flex items-center gap-1.5 rounded-xl px-2 py-1.5">
        <label className="flex items-center gap-1 text-xs">
          <Maximize2
            className="h-3 w-3 text-muted-foreground"
            aria-hidden="true"
          />
          <span className="sr-only">Size for {title}</span>
          <NativeSelect
            value={instance.size}
            onChange={(e) =>
              onResize?.(id, e.target.value as WidgetSize)
            }
            className="h-7 text-xs"
            data-testid={`editor-widget-size-${instance.widgetId}`}
            aria-label={`Size for ${title}`}
          >
            {ALL_SIZES.filter((s) => supported.includes(s)).map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </NativeSelect>
        </label>
        <span className="t-mono text-[10px] text-muted-foreground ml-auto">
          order {index}
        </span>
        {onConfigure && (
          <Button
            size="icon-sm"
            variant="ghost"
            onClick={() => onConfigure(id)}
            aria-label={`Configure ${title}`}
            data-testid={`editor-widget-configure-${instance.widgetId}`}
          >
            <SettingsIcon className="h-3.5 w-3.5" aria-hidden="true" />
          </Button>
        )}
        {onDuplicate && (
          <Button
            size="icon-sm"
            variant="ghost"
            onClick={() => onDuplicate(id)}
            aria-label={`Duplicate ${title}`}
            data-testid={`editor-widget-duplicate-${instance.widgetId}`}
          >
            <Copy className="h-3.5 w-3.5" aria-hidden="true" />
          </Button>
        )}
        {onRemove && (
          <Button
            size="icon-sm"
            variant="ghost"
            onClick={() => onRemove(id)}
            aria-label={`Delete ${title}`}
            data-testid={`editor-widget-delete-${instance.widgetId}`}
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
          </Button>
        )}
      </div>
    </>
  );
};

export default WidgetCanvas;
